/*
 *
 * Copyright (c) 2025 Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Alexander Orlov ("CONFIDENTIAL INFORMATION").
 *
 * Use, reproduction, modification, or distribution of this code
 * is permitted only pursuant to a written license expressly granted
 * by Alexander Orlov.
 *
 * You shall not disclose such confidential information and shall use
 * it only in accordance with the terms of the written license agreement
 * you have entered into with Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft
 *
 */


import React, { useState } from 'react';
import { cn } from '@/utils';

export interface Tab {
  id: string;
  label: string;
  content?: React.ReactNode;
  disabled?: boolean;
}

interface TabsProps {
  tabs: Tab[];
  defaultTab?: string;
  activeTab?: number;
  onChange?: (index: number) => void;
  className?: string;
}

export const Tabs: React.FC<TabsProps> = ({
  tabs,
  defaultTab,
  activeTab: controlledActiveTab,
  onChange,
  className,
}) => {
  const [internalActiveIndex, setInternalActiveIndex] = useState(
    defaultTab ? tabs.findIndex(t => t.id === defaultTab) : 0
  );
  
  // Support both controlled (numeric index) and uncontrolled modes
  const isControlled = typeof controlledActiveTab === 'number';
  const activeIndex = isControlled ? controlledActiveTab : internalActiveIndex;

  const handleTabClick = (index: number) => {
    if (!isControlled) {
      setInternalActiveIndex(index);
    }
    onChange?.(index);
  };

  const activeContent = tabs[activeIndex]?.content;

  return (
    <div className={cn('w-full', className)}>
      {/* Tab buttons - scrollable on mobile */}
      <div className="border-b border-gray-200 overflow-x-auto scrollbar-hide">
        <nav className="flex md:space-x-8 space-x-2 px-1" aria-label="Tabs">
          {tabs.map((tab, index) => (
            <button
              key={tab.id}
              onClick={() => !tab.disabled && handleTabClick(index)}
              className={cn(
                'py-3 md:py-4 px-3 md:px-1 border-b-2 text-xs md:text-sm transition-colors whitespace-nowrap',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-1 rounded-t',
                activeIndex === index
                  ? 'border-primary-600 text-primary-600 font-semibold bg-primary-50/60 rounded-t-md shadow-[0_4px_10px_-4px_rgba(37,99,235,0.45)]'
                  : 'border-transparent text-gray-500 font-medium hover:text-gray-700 hover:border-gray-300',
                tab.disabled && 'opacity-50 cursor-not-allowed'
              )}
              disabled={tab.disabled}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content - only render if content is provided */}
      {activeContent && <div className="mt-4">{activeContent}</div>}
    </div>
  );
};
